// Shared helpers for the document collection view. Uploaded files carry
// { file_name, original_name, size, path } — the file_name embeds the upload
// date as DD/MM/YYYY (e.g. Monica_Das_Payslips_23/07/2026_1.png).

const IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp', 'svg'];
const MIME_BY_EXT = {
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
    bmp: 'image/bmp',
    svg: 'image/svg+xml',
    pdf: 'application/pdf',
};

export const getExtension = (name = '') => {
    const clean = name.split('?')[0];
    const dot = clean.lastIndexOf('.');
    return dot === -1 ? '' : clean.slice(dot + 1).toLowerCase();
};

// image files render as real thumbnails/previews; pdf renders in an iframe;
// everything else falls back to a typed icon.
export const getFileKind = (name) => {
    const ext = getExtension(name);
    if (IMAGE_EXTS.includes(ext)) return 'image';
    if (ext === 'pdf') return 'pdf';
    return 'other';
};

export const getMimeType = (name) => MIME_BY_EXT[getExtension(name)] || 'application/octet-stream';

export const isPreviewable = (name) => {
    const kind = getFileKind(name);
    return kind === 'image' || kind === 'pdf';
};

export const formatBytes = (bytes) => {
    if (bytes == null || Number.isNaN(bytes)) return '';
    if (bytes < 1024) return `${bytes} B`;
    const units = ['KB', 'MB', 'GB'];
    let value = bytes / 1024;
    let i = 0;
    while (value >= 1024 && i < units.length - 1) {
        value /= 1024;
        i += 1;
    }
    return `${value.toFixed(value >= 10 || i === 0 ? 0 : 1)} ${units[i]}`;
};

// Pull the DD/MM/YYYY the uploader embeds into the file name, if present.
export const getUploadDate = (fileName = '') => {
    const match = fileName.match(/(\d{2})\/(\d{2})\/(\d{4})/);
    if (!match) return '';
    return `${match[1]}/${match[2]}/${match[3]}`;
};

// Millisecond timestamp for sorting. Prefers the recorded uploaded_at, then a
// 'YYYY-MM-DD' uploaded_date (bank statement groups), then the DD/MM/YYYY in the
// file name, then 0 so undated files sort last.
export const getUploadMillis = (file = {}) => {
    if (file.uploaded_at) {
        const t = new Date(file.uploaded_at).getTime();
        if (!Number.isNaN(t)) return t;
    }
    if (file.uploaded_date) {
        const t = new Date(file.uploaded_date).getTime();
        if (!Number.isNaN(t)) return t;
    }
    const date = getUploadDate(file.file_name);
    if (date) {
        const [d, m, y] = date.split('/');
        const t = new Date(`${y}-${m}-${d}`).getTime();
        if (!Number.isNaN(t)) return t;
    }
    return 0;
};

const pad = (n) => String(n).padStart(2, '0');

// Prefer the exact uploaded_at timestamp (recorded on newer uploads); fall
// back to the DD/MM/YYYY embedded in the file name for older files that have
// no timestamp. Returns { date, time } where time is '' when unknown.
export const getUploadStamp = (file = {}) => {
    if (file.uploaded_at) {
        const d = new Date(file.uploaded_at);
        if (!Number.isNaN(d.getTime())) {
            return {
                date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
                time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
            };
        }
    }
    // Bank statement groups record only a 'YYYY-MM-DD' upload date (no time).
    if (file.uploaded_date) {
        const [y, m, d] = file.uploaded_date.split('-');
        if (y && m && d) return { date: `${d}/${m}/${y}`, time: '' };
    }
    return { date: getUploadDate(file.file_name), time: '' };
};
